/**
 * CountryFlag — small circular country-flag badge.
 *
 * Moved from `widgets/ihs/IhsPrimitives.tsx` (Phase 0b). Uses the
 * public flagcdn.com PNG service. Maps both ISO 3166-1 alpha-2 and
 * alpha-3 codes via an inline table. Returns `null` when the code is
 * unknown so widgets can render with no flag rather than a broken
 * square.
 *
 * Image is clipped to a circle via CSS so the 4:3 source crops cleanly.
 *
 * Shape: { code: string | null; size?: number }
 */

import type { ReactElement } from 'react';
import { z } from 'zod';
import type { PrimitiveMeta } from './_meta.js';
import styles from './CountryFlag.module.css';

const dataShape = z.object({
  /** ISO 3166-1 alpha-2 OR alpha-3 country code. null → renders nothing. */
  code: z.string().nullable(),
  /** Diameter in CSS px. Default 16. */
  size: z.number().int().positive().optional(),
});

export const META: PrimitiveMeta = {
  name: 'country-flag',
  description: 'Small circular country-flag badge (alpha-2 / alpha-3 input).',
  composable: true,
  dataShape,
  densities: ['compact'],
  breakpoints: { mobile: true, wide: true },
  animation: 'none',
};

/**
 * ISO 3166-1 alpha-3 → alpha-2 mapping for the country codes Sportradar
 * actually emits across player nationality + referee country fields. Kept
 * inline so the flag primitive has no extra-file footprint. Extend as new
 * countries show up in our feeds.
 */
const ALPHA3_TO_2: Record<string, string> = {
  BEL: 'BE', NOR: 'NO', ENG: 'GB', FRA: 'FR', GER: 'DE', ESP: 'ES',
  ITA: 'IT', BRA: 'BR', ARG: 'AR', POR: 'PT', NED: 'NL', DEN: 'DK',
  SWE: 'SE', NGA: 'NG', NZL: 'NZ', COL: 'CO', URU: 'UY', CHL: 'CL',
  KOR: 'KR', JPN: 'JP', USA: 'US', MEX: 'MX', CMR: 'CM', GHA: 'GH',
  EGY: 'EG', MAR: 'MA', SEN: 'SN', ISL: 'IS', IRL: 'IE', SCO: 'GB',
  WAL: 'GB', NIR: 'GB', RUS: 'RU', UKR: 'UA', POL: 'PL', CZE: 'CZ',
  AUT: 'AT', SUI: 'CH', CRO: 'HR', SRB: 'RS', BIH: 'BA', GRE: 'GR',
  TUR: 'TR', BGR: 'BG', ROU: 'RO', HUN: 'HU', SVK: 'SK', SVN: 'SI',
  FIN: 'FI', EST: 'EE', LVA: 'LV', LTU: 'LT', ALB: 'AL',
};

function alpha3to2(code: string): string | null {
  return ALPHA3_TO_2[code.toUpperCase()] ?? null;
}

interface CountryFlagProps {
  code: string | null;
  /** Diameter in CSS px. Default 16. */
  size?: number;
}

export function CountryFlag({
  code,
  size = 16,
}: CountryFlagProps): ReactElement | null {
  if (!code) return null;
  const trimmed = code.trim();
  if (trimmed.length === 0) return null;
  const cc = trimmed.length === 3 ? alpha3to2(trimmed) : trimmed;
  if (!cc) return null;
  const src = `https://flagcdn.com/w${size * 2}/${cc.toLowerCase()}.png`;
  const flagCircleClass = styles['flagCircle'] ?? '';
  return (
    <span
      className={flagCircleClass}
      style={{ width: `${size}px`, height: `${size}px` }}
    >
      <img src={src} alt={code} loading="lazy" />
    </span>
  );
}

export default CountryFlag;
